import {
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArticleOutlined as ArticleOutlinedIcon,
  EditNoteOutlined as EditNoteOutlinedIcon,
  EngineeringOutlined as EngineeringOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  DoneAllOutlined as DoneAllOutlinedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  Search as SearchOutlinedIcon,
  RefreshOutlined as RefreshOutlinedIcon,
} from "../../../components/icons";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useEffect } from "react";
import Loader from "../../../components/Loader";
import { useToast } from "../../../hooks/useToast";
import { formatR } from "../../../utils/money";
import {
  TombstoneItem,
  getTombstoneId,
  getTombstoneStatus,
  useTombstoneStore,
} from "../../../store/useTombstoneStore";
import {
  OperationsEmptyState,
  OperationsPageShell,
  ifasColors,
} from "../ifas/components/OperationsUI";
import {
  StandardCountBadge,
  StandardMetricCard,
  StandardPager,
  StatusDotChip,
  Tone,
  standardGhostBtnSx,
  standardOutlineBtnSx,
  standardTableHeadCellSx,
  standardTableRowSx,
} from "../ifas/components/StandardUI";

// Tombstone lifecycle -> tone, matching the system-wide status vocabulary
// (TombstoneStatus.java: DRAFT / IN_PROGRESS / CONFIRMED / COMPLETED).
const TOMBSTONE_STATUS_TONE: Record<string, Tone> = {
  DRAFT: "amber",
  IN_PROGRESS: "blue",
  CONFIRMED: "purple",
  COMPLETED: "green",
};

const STATUS_OPTIONS = ["DRAFT", "IN_PROGRESS", "CONFIRMED", "COMPLETED"];

const humanizeStatus = (value?: string) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (date?: string) =>
  date ? dayjs(date).format("YYYY-MM-DD") : "Not set";

const getDisplayValue = (value?: string | null) => value || "Not captured";

const getBenefitLabel = (row: TombstoneItem) => {
  if (row.cashPayoutOnly && row.serviceRequired) return "Cash + Service";
  if (row.cashPayoutOnly) return "Cash payout";
  if (row.serviceRequired) return "Service required";
  return "Not selected";
};

const getBenefitTone = (label: string): Tone => {
  if (label === "Cash payout") return "amber";
  if (label === "Not selected") return "grey";
  return "blue";
};

export default function TombstonePortal({
  fetchTombstones,
  currentPage,
  rowsPerPage,
  setCurrentPage,
  onSelectTombstone,
}: any) {
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const { tombstones, totalCount, statusCounts } = useTombstoneStore();
  const { showInfoToast } = useToast();

  const fetchAllTombstones = async (page = 0, size = 10) => {
    setIsLoading(true);
    const response = await fetchTombstones(page, size);

    if (response?.status === 200) {
      useTombstoneStore.getState().setTombstones(response.data);
    } else {
      showInfoToast(response?.data?.message || "Failed to load tombstones");
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (fetchTombstones) {
      fetchAllTombstones(currentPage, rowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTombstones, currentPage, rowsPerPage]);

  const handleOpenDetails = (item: TombstoneItem | null) => {
    const id = getTombstoneId(item);

    if (!id) {
      showInfoToast("Unable to identify the selected tombstone");
      return;
    }

    useTombstoneStore.getState().setSelectedTombstone(item);
    onSelectTombstone(id);
  };

  // Real search/status filtering over the currently loaded page — the portal
  // received no filter controls at all before this pass.
  const visibleTombstones = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return tombstones.filter((row) => {
      if (statusFilter && getTombstoneStatus(row) !== statusFilter) {
        return false;
      }

      if (!query) return true;

      const haystack = [
        row.tombstoneNumber,
        row.iceClaimNumber,
        row.clientFullName,
        row.deceasedFullName,
        row.stoneCode,
        row.policyNumber,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");

      return haystack.includes(query);
    });
  }, [tombstones, searchText, statusFilter]);

  // KPI buckets derived exhaustively from TombstoneStatus.java — Total plus
  // one card per real status, summing to Total. No CANCELLED branch (the
  // backend enum has none).
  const metrics = [
    {
      label: "Total",
      value: totalCount || 0,
      caption: "All tombstone requests",
      tone: "orange" as Tone,
      icon: <ArticleOutlinedIcon />,
    },
    {
      label: "Draft",
      value: statusCounts?.DRAFT || 0,
      caption: "Being captured",
      tone: "amber" as Tone,
      icon: <EditNoteOutlinedIcon />,
    },
    {
      label: "In Progress",
      value: statusCounts?.IN_PROGRESS || 0,
      caption: "Stone & install in motion",
      tone: "blue" as Tone,
      icon: <EngineeringOutlinedIcon />,
    },
    {
      label: "Confirmed",
      value: statusCounts?.CONFIRMED || 0,
      caption: "Locked for installation",
      tone: "purple" as Tone,
      icon: <CheckCircleOutlineIcon />,
    },
    {
      label: "Completed",
      value: statusCounts?.COMPLETED || 0,
      caption: "Installed on site",
      tone: "green" as Tone,
      icon: <DoneAllOutlinedIcon />,
    },
  ];

  return (
    <OperationsPageShell
      eyebrow="Memorial Services"
      title="Tombstone Portal"
      description="Manage tombstone requests with a respectful view of client details, stone selection, cemetery information, and installation progress."
      actions={
        <Button
          variant="outlined"
          startIcon={<RefreshOutlinedIcon />}
          onClick={() => fetchAllTombstones(currentPage, rowsPerPage)}
          sx={standardGhostBtnSx}
        >
          Refresh
        </Button>
      }
    >
      {isLoading ? <Loader isLoading={isLoading} /> : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(5, minmax(0, 1fr))",
          },
          gap: 1.75,
          mb: 2.5,
        }}
      >
        {metrics.map((metric) => (
          <StandardMetricCard
            key={metric.label}
            label={metric.label}
            value={metric.value}
            caption={metric.caption}
            tone={metric.tone}
            icon={metric.icon}
          />
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2.25,
          mb: 2,
          border: `1px solid ${ifasColors.border}`,
          borderRadius: "16px",
          background: `linear-gradient(135deg, ${ifasColors.softOrange}, ${ifasColors.surface} 46%)`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                bgcolor: ifasColors.softOrange,
                color: ifasColors.orange,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <PlaceOutlinedIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 19, fontWeight: 900, color: ifasColors.text }}>
                Recent tombstones
              </Typography>
              <Typography sx={{ mt: 0.4, color: ifasColors.muted, fontSize: 12.5, lineHeight: 1.45, maxWidth: 520 }}>
                Search by client, deceased or stone code, or filter to a single status.
              </Typography>
            </Box>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              size="small"
              placeholder="Search client, deceased, tombstone number"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              InputProps={{ startAdornment: <SearchOutlinedIcon sx={{ mr: 1, color: ifasColors.muted }} /> }}
              sx={{ minWidth: 280, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            <TextField
              size="small"
              select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 190, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {humanizeStatus(option)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${ifasColors.border}`,
          borderRadius: "16px",
          overflow: "hidden",
          bgcolor: ifasColors.surface,
          boxShadow: "0 4px 14px rgba(20,24,31,.05)",
        }}
      >
        <Box sx={{ px: 2.5, py: 2.25, borderBottom: `1px solid ${ifasColors.border}`, display: "flex", alignItems: "center", gap: 1.25 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 800, color: ifasColors.text }}>Recent Tombstones</Typography>
          <StandardCountBadge count={totalCount || 0} />
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 1120 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={standardTableHeadCellSx}>Tombstone</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Policy</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Client</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Deceased</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Stone</TableCell>
                <TableCell align="right" sx={standardTableHeadCellSx}>Price</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Installation</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Benefit</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Status</TableCell>
                <TableCell align="right" sx={standardTableHeadCellSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTombstones.map((row, index) => {
                const status = getTombstoneStatus(row);
                const benefitLabel = getBenefitLabel(row);
                const stonePrice = Number(row.stonePrice) || 0;
                const kmCharge = Number(row.kilometerCharge) || 0;
                const total = stonePrice + kmCharge;

                return (
                  <TableRow
                    key={getTombstoneId(row) || index}
                    hover
                    sx={standardTableRowSx}
                    onClick={() => handleOpenDetails(row)}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography fontWeight={900} color={ifasColors.text}>
                        {getDisplayValue(row.tombstoneNumber)}
                      </Typography>
                      <Typography fontSize={11.5} fontWeight={600} color={ifasColors.muted} mt={0.3}>
                        {getDisplayValue(row.iceClaimNumber)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={900} color={ifasColors.text}>
                        {getDisplayValue(row.policyNumber)}
                      </Typography>
                      <Typography fontSize={11.5} fontWeight={600} color={ifasColors.muted} mt={0.3}>
                        {getDisplayValue(row.branchName)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} color={ifasColors.text}>
                        {getDisplayValue(row.clientFullName)}
                      </Typography>
                      <Typography fontSize={11.5} fontWeight={600} color={ifasColors.muted} mt={0.3}>
                        {getDisplayValue(row.clientContactNumbers)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                      {getDisplayValue(row.deceasedFullName)}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} color={ifasColors.text}>
                        {getDisplayValue(row.stoneType)}
                      </Typography>
                      <Typography fontSize={11.5} fontWeight={600} color={ifasColors.muted} mt={0.3}>
                        {getDisplayValue(row.stoneCode)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={900} color={ifasColors.text}>
                        {formatR(total)}
                      </Typography>
                      <Typography fontSize={11.5} fontWeight={600} color={ifasColors.muted} mt={0.3}>
                        {kmCharge ? `${formatR(stonePrice)} + ${formatR(kmCharge)} km` : "No km charge"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} color={ifasColors.text}>
                        {formatDate(row.installationDate)}
                      </Typography>
                      <Typography fontSize={11.5} fontWeight={600} color={ifasColors.muted} mt={0.3}>
                        {getDisplayValue(row.cemeteryName)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusDotChip status={benefitLabel} tone={getBenefitTone(benefitLabel)} />
                    </TableCell>
                    <TableCell>
                      <StatusDotChip status={status} tone={TOMBSTONE_STATUS_TONE[status] || "grey"} />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        sx={{ ...standardOutlineBtnSx, minHeight: 34, px: 1.6 }}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenDetails(row);
                        }}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}

              {visibleTombstones.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <OperationsEmptyState
                      title="No tombstone records found"
                      description={
                        tombstones.length === 0
                          ? "Tombstone requests will appear here once they are captured."
                          : "No tombstone matches the current search or status filter."
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <StandardPager
          page={currentPage}
          total={totalCount || 0}
          pageSize={rowsPerPage}
          onPage={setCurrentPage}
        />
      </Paper>
    </OperationsPageShell>
  );
}
